import { useMemo } from 'react';

import { LightbulbIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Separator } from '@/components/ui/separator';
import { useComparisonMode } from '@/modules/analytics/hooks/useComparisonMode';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';

import { formatDateRangeLabel } from '../../../utils/formatDateRangeLabel';
import { AnalyticsEmptyState } from '../AnalyticsEmptyState';
import { CategoricalInsightBarCard } from './CategoricalInsightBarCard';
import { CustomInsightBooleanCard } from './CustomInsightBooleanCard';
import { CustomInsightCountCard } from './CustomInsightCountCard';
import { DateTimeLineInsightCard } from './DateTimeLineInsightCard';
import { getComparisonPeriodKey, PercentChangeIndicator } from '../PercentChangeIndicator';
import { useSingleAgentCustomInsights } from './hooks/useSingleAgentCustomInsights';

/**
 * Custom insight categories for a single selected AI Agent. Each insight
 * type (categorical, datetime, boolean, contact) returns N rows from the
 * backend (one per `insight_name`); we render one card per row so the page
 * scales to whatever insights the brain has configured.
 */
export const AiAgentCustomInsightsSection = () => {
  const { t } = useTranslation();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { isComparing } = useComparisonMode();
  const { data: customInsights } = useSingleAgentCustomInsights();

  const dateRangeLabel = useMemo(
    () => formatDateRangeLabel(startDate, endDate),
    [startDate, endDate]
  );

  const comparisonLabel = t(getComparisonPeriodKey(startDate, endDate));

  const hasAnyInsights =
    customInsights.contact.length > 0 ||
    customInsights.boolean.length > 0 ||
    customInsights.categorical.length > 0 ||
    customInsights.datetime.length > 0;

  return (
    <section className="space-y-6" aria-label={t('analytics.custom_insights_section_a11y')}>
      <div className="flex items-center gap-4 px-4 py-2">
        <Separator className="flex-1" />
        <div className="flex items-center gap-1.5">
          <LightbulbIcon className="size-4 text-muted-foreground" />
          <span className="text-sm leading-5 text-muted-foreground">{t('common.insights')}</span>
        </div>
        <Separator className="flex-1" />
      </div>

      {!hasAnyInsights && (
        <AnalyticsEmptyState
          icon="lightbulb"
          title={t('analytics.custom_insights_empty_title')}
          description={t('analytics.custom_insights_empty_description')}
          className="py-6"
        />
      )}

      {customInsights.categorical.map((insight) => (
        <CategoricalInsightBarCard
          key={`categorical-${insight.insightName}`}
          dateRangeLabel={dateRangeLabel}
          insight={insight}
        />
      ))}

      {(customInsights.contact.length > 0 || customInsights.boolean.length > 0) && (
        <div className="flex flex-wrap items-stretch gap-4">
          {customInsights.contact.map((insight) => {
            const previous = customInsights.previousContactByName.get(insight.insightName);
            const Icon = insight.kind === 'phone_number' ? PhoneIcon : MailIcon;
            const sublabel =
              insight.kind === 'phone_number'
                ? t('analytics.custom_insight_phone_suffix')
                : t('analytics.custom_insight_email_suffix');
            return (
              <CustomInsightCountCard
                key={`contact-${insight.insightName}`}
                label={insight.insightName}
                count={insight.collectedCount}
                sublabel={sublabel}
                icon={Icon}
                className="lg:flex-1"
                comparison={
                  isComparing ? (
                    <PercentChangeIndicator
                      current={insight.collectedCount}
                      previous={previous?.collectedCount ?? 0}
                      comparisonLabel={comparisonLabel}
                    />
                  ) : null
                }
              />
            );
          })}

          {customInsights.boolean.map((insight) => {
            const previous = customInsights.previousBooleanByName.get(insight.insightName);
            return (
              <CustomInsightBooleanCard
                key={`boolean-${insight.insightName}`}
                label={insight.insightName}
                valueLabel={t('common.percentage', { 0: insight.truePercent })}
                className="lg:flex-1"
                comparison={
                  isComparing ? (
                    <PercentChangeIndicator
                      current={insight.truePercent}
                      previous={previous?.truePercent ?? 0}
                      comparisonLabel={comparisonLabel}
                    />
                  ) : null
                }
              />
            );
          })}
        </div>
      )}

      {customInsights.datetime.map((insight) => (
        <DateTimeLineInsightCard
          key={`datetime-${insight.insightName}`}
          title={insight.insightName}
          dateRangeLabel={dateRangeLabel}
          series={insight.series}
        />
      ))}
    </section>
  );
};
